import React, { useEffect, useState } from 'react';
import { Card, CardTitle } from '../../../../../components/Card';
import { InfoTooltip } from '../../../../../components/InfoTooltip';
import { LabelledSelect } from '../../../../../components/LabelledSelect';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { SelectOption } from '../../../../../components/Select';
import {
  StageHeatmap,
  HeatmapCell,
} from '../../../../../entities/applicant_tracking/analytics/StageHeatmap';
import { StageHeatmapService } from '../../../../../services/applicant_tracking/analytics/StageHeatmapService';
import { buildPassThroughRateValue } from '../../../../../utils/applicant_tracking/analytics';
import { WidgetEmptyStateContent } from '../../../../survey_engine/WidgetEmptyStateContent';
import { FilterState } from '../../Filters';

interface PropTypes {
  filterState?: FilterState;
  groupBy: string;
  onGroupByChange: (groupBy: string) => void;
}

const GROUP_BY_OPTIONS: SelectOption[] = [
  { label: 'Department', value: 'department' },
  { label: 'Jobs', value: 'job' },
  { label: 'Hiring Managers', value: 'hiring_manager' },
  { label: 'Recruiters', value: 'recruiter' },
  { label: 'Source', value: 'source' },
];

function getHeatmapColor(value: number, min: number, max: number): string {
  if (max === min) return '#EDE9FE';

  const ratio = (value - min) / (max - min);

  const r = Math.round(237 - ratio * (237 - 79));
  const g = Math.round(233 - ratio * (233 - 70));
  const b = Math.round(254 - ratio * (254 - 229));

  return `rgb(${r}, ${g}, ${b})`;
}

function getCellValue(cell: HeatmapCell, viewMode: 'count' | 'ptr'): number {
  if (viewMode === 'count') return cell.candidateCount;

  return cell.passThroughRate ?? 0;
}

function getMinMax(
  data: StageHeatmap,
  viewMode: 'count' | 'ptr',
): { min: number; max: number } {
  const values = data.rows.flatMap((row) =>
    row.cells.map((cell) => getCellValue(cell, viewMode)),
  );

  if (values.length === 0) return { min: 0, max: 0 };

  return { min: Math.min(...values), max: Math.max(...values) };
}

function formatCellContent(
  cell: HeatmapCell,
  viewMode: 'count' | 'ptr',
): React.ReactNode {
  if (viewMode === 'count') {
    const ptr =
      cell.passThroughRate != null
        ? ` (${buildPassThroughRateValue(cell.passThroughRate)}%)`
        : '';
    return (
      <span>
        {cell.candidateCount}
        {ptr && <span style={{ opacity: 0.8 }}>{ptr}</span>}
      </span>
    );
  }

  return cell.passThroughRate != null
    ? `${buildPassThroughRateValue(cell.passThroughRate)}%`
    : '-';
}

export function StageHeatmapWidget(props: PropTypes) {
  const { groupBy, onGroupByChange } = props;
  const [data, setData] = useState<StageHeatmap | null>(null);
  const [viewMode, setViewMode] = useState<'count' | 'ptr'>('count');

  useEffect(() => {
    if (!props.filterState) return;

    setData(null);
    const controller = new AbortController();

    StageHeatmapService.show(props.filterState, controller.signal, groupBy)
      .then(setData)
      .catch((e) => {
        if (e.name !== 'AbortError') throw e;
      });

    return () => controller.abort();
  }, [JSON.stringify(props.filterState), groupBy]);

  const selectedGroupBy = GROUP_BY_OPTIONS.find((o) => o.value === groupBy);

  return (
    <Card>
      <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3'>
        <div className='d-flex align-items-center gap-2'>
          <CardTitle>Grouped PTR by Stage</CardTitle>
          <InfoTooltip>
            Shows pass-through rates grouped by Departments, Jobs, Hiring
            Managers, Recruiters, or Channels.
          </InfoTooltip>
        </div>
      </div>
      <div className='d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3'>
        <div style={{ maxWidth: '220px' }}>
          <LabelledSelect
            label='Group by'
            options={GROUP_BY_OPTIONS}
            selected={selectedGroupBy}
            isClearable={false}
            onChange={(option) => onGroupByChange(option.value)}
            size='sm'
          />
        </div>
        <div className='d-flex gap-2'>
          <button
            className={`btn btn-sm ${viewMode === 'count' ? 'text-white' : ''}`}
            style={{
              borderRadius: '20px',
              padding: '6px 16px',
              fontSize: '13px',
              fontWeight: 500,
              backgroundColor: viewMode === 'count' ? '#6C47FF' : 'transparent',
              border:
                viewMode === 'count'
                  ? '1px solid #6C47FF'
                  : '1px solid #D1D5DB',
              color: viewMode === 'count' ? '#FFFFFF' : '#6B7280',
            }}
            onClick={() => setViewMode('count')}
          >
            Total Candidates per Stage
          </button>
          <button
            className={`btn btn-sm ${viewMode === 'ptr' ? 'text-white' : ''}`}
            style={{
              borderRadius: '20px',
              padding: '6px 16px',
              fontSize: '13px',
              fontWeight: 500,
              backgroundColor: viewMode === 'ptr' ? '#6C47FF' : 'transparent',
              border:
                viewMode === 'ptr' ? '1px solid #6C47FF' : '1px solid #D1D5DB',
              color: viewMode === 'ptr' ? '#FFFFFF' : '#6B7280',
            }}
            onClick={() => setViewMode('ptr')}
          >
            Pass-Through Rate Comparison
          </button>
        </div>
      </div>
      <HeatmapContent data={data} viewMode={viewMode} />
    </Card>
  );
}

function HeatmapContent(props: {
  data: StageHeatmap | null;
  viewMode: 'count' | 'ptr';
}) {
  if (!props.data) return <LoadingSpinner />;

  if (props.data.rows.length === 0) return <WidgetEmptyStateContent />;

  return <HeatmapTable data={props.data} viewMode={props.viewMode} />;
}

function HeatmapTable(props: {
  data: StageHeatmap;
  viewMode: 'count' | 'ptr';
}) {
  const { data, viewMode } = props;
  const { min, max } = getMinMax(data, viewMode);

  return (
    <div>
      <div className='table-responsive'>
        <table
          style={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: '4px',
            fontSize: '13px',
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  padding: '8px 12px',
                  fontWeight: 400,
                  color: '#6B7280',
                }}
              />
              {data.stages.map((stage) => (
                <th
                  key={stage.stageId}
                  style={{
                    padding: '8px 12px',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    fontWeight: 400,
                    color: '#6B7280',
                  }}
                >
                  {stage.stageName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={row.groupId}>
                <td
                  style={{
                    padding: '8px 12px',
                    whiteSpace: 'nowrap',
                    fontWeight: 500,
                    color: '#374151',
                  }}
                >
                  {row.groupName}
                </td>
                {row.cells.map((cell) => {
                  const value = getCellValue(cell, viewMode);
                  const backgroundColor = getHeatmapColor(value, min, max);
                  const ratio = max !== min ? (value - min) / (max - min) : 0;
                  const textColor = ratio > 0.55 ? '#FFFFFF' : '#1F2937';

                  return (
                    <td
                      key={cell.stageId}
                      style={{
                        textAlign: 'center',
                        padding: '10px 12px',
                        backgroundColor,
                        color: textColor,
                        borderRadius: '6px',
                        fontWeight: 500,
                      }}
                    >
                      {formatCellContent(cell, viewMode)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <HeatmapLegend />
    </div>
  );
}

const LEGEND_STEPS = [
  { label: '0%', color: '#EDE9FE' },
  { label: '20%', color: '#C4B5FD' },
  { label: '40%', color: '#A78BFA' },
  { label: '60%', color: '#7C3AED' },
  { label: '80%', color: '#5B21B6' },
];

function HeatmapLegend() {
  return (
    <div
      className='d-flex align-items-center mt-3'
      style={{ fontSize: '12px', color: '#6B7280' }}
    >
      {LEGEND_STEPS.map((step) => (
        <div key={step.label} style={{ flex: 1 }}>
          <div style={{ marginBottom: '4px' }}>{step.label}</div>
          <div
            style={{
              height: '8px',
              backgroundColor: step.color,
              borderRadius: '4px',
              marginRight: '3px',
            }}
          />
        </div>
      ))}
    </div>
  );
}
